import React from 'react';
import { useResponsive } from '../../hooks/useResponsive';

interface ValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApprove: () => void;
  onReject: (reason: string) => void;
  reporterName: string;
  stats: { goals: number; assists: number; saves: number };
  isLoading?: boolean;
}

export const ValidationModal: React.FC<ValidationModalProps> = ({
  isOpen,
  onClose,
  onApprove,
  onReject,
  reporterName,
  stats,
  isLoading = false
}) => {
  const { isMobile } = useResponsive();
  const [showRejectionForm, setShowRejectionForm] = React.useState(false);
  const [rejectionReason, setRejectionReason] = React.useState('');

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      alert('Por favor ingresa un motivo para rechazar el reporte');
      return;
    }
    onReject(rejectionReason.trim());
    setRejectionReason('');
    setShowRejectionForm(false);
  };

  const handleClose = () => {
    setRejectionReason('');
    setShowRejectionForm(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '10px' : '20px'
    }}>
      <div style={{
        backgroundColor: '#23272A',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '16px' : '24px',
        maxWidth: isMobile ? 'calc(100vw - 20px)' : '500px',
        width: '100%',
        maxHeight: isMobile ? 'calc(100vh - 20px)' : '80vh',
        border: '1px solid #33383B',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        overflowY: 'auto'
      }}>
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <h2 style={{ 
            color: '#FFFFFF', 
            margin: '0 0 8px 0', 
            fontSize: isMobile ? '18px' : '20px',
            fontWeight: 'bold'
          }}>
            ✅ Validar Reporte
          </h2>
          <p style={{ 
            color: '#B0BEC5', 
            margin: 0, 
            fontSize: isMobile ? '13px' : '14px',
            lineHeight: '1.4'
          }}>
            Revisa las estadísticas reportadas por tu compañero
          </p>
        </div>

        <div style={{
          backgroundColor: '#2C2F33',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          border: '1px solid #40444B'
        }}>
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: '#1B5E20',
            borderRadius: '8px',
            border: '1px solid #2E7D32'
          }}>
            <div style={{ color: '#FFFFFF', fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>
              {reporterName}
            </div>
            <div style={{ color: '#B0BEC5', fontSize: '14px' }}>
              Solicita validación de sus estadísticas
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#FFFFFF',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '600',
              marginBottom: '8px'
            }}>
              ⚽ Goles:
            </div>
            <div style={{
              padding: '12px',
              backgroundColor: '#181A1B',
              borderRadius: '8px',
              border: '1px solid #40444B',
              color: '#FFFFFF',
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              {stats.goals}
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#FFFFFF',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '600',
              marginBottom: '8px'
            }}>
              ➡️⚽ Asistencias:
            </div>
            <div style={{
              padding: '12px',
              backgroundColor: '#181A1B',
              borderRadius: '8px',
              border: '1px solid #40444B',
              color: '#FFFFFF',
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              {stats.assists}
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#FFFFFF',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '600',
              marginBottom: '8px'
            }}>
              🧤⚽ Atajadas:
            </div>
            <div style={{
              padding: '12px',
              backgroundColor: '#181A1B',
              borderRadius: '8px',
              border: '1px solid #40444B',
              color: '#FFFFFF',
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              {stats.saves}
            </div>
          </div>

          <div style={{
            backgroundColor: '#FF9800',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '16px',
            border: '1px solid #F57C00'
          }}>
            <div style={{ color: '#181A1B', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
              ⚠️ Tu Responsabilidad
            </div>
            <div style={{ color: '#181A1B', fontSize: '12px', lineHeight: '1.4' }}>
              Al aprobar este reporte, confirmas que las estadísticas son correctas. 
              Una vez aprobado, no se podrán modificar.
            </div>
          </div>
        </div>

        {showRejectionForm && (
          <div style={{
            backgroundColor: '#2C2F33',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            border: '1px solid #40444B'
          }}>
            <div style={{ 
              color: '#FF1744', 
              fontSize: '14px', 
              fontWeight: '600', 
              marginBottom: '12px' 
            }}>
              ❌ Motivo del Rechazo
            </div>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Ej: Hay un gol de más, falta una asistencia, etc."
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '12px',
                backgroundColor: '#181A1B',
                border: '1px solid #40444B',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'space-between'
        }}>
          {!showRejectionForm ? (
            <>
              <button
                onClick={() => setShowRejectionForm(true)}
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: isMobile ? '10px 16px' : '10px 20px',
                  backgroundColor: '#FF1744',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '600',
                  minHeight: isMobile ? '44px' : 'auto',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                ❌ Rechazar
              </button>
              <button
                onClick={handleClose}
                disabled={isLoading}
                style={{
                  padding: isMobile ? '10px 16px' : '10px 20px',
                  backgroundColor: '#40444B',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '600',
                  minHeight: isMobile ? '44px' : 'auto'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={onApprove}
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: isMobile ? '10px 16px' : '10px 20px',
                  backgroundColor: '#4CAF50',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '600',
                  minHeight: isMobile ? '44px' : 'auto',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                {isLoading ? 'Procesando...' : '✅ Aprobar'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setShowRejectionForm(false)}
                disabled={isLoading}
                style={{
                  padding: isMobile ? '10px 16px' : '10px 20px',
                  backgroundColor: '#40444B',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '600',
                  minHeight: isMobile ? '44px' : 'auto'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: isMobile ? '10px 16px' : '10px 20px',
                  backgroundColor: '#FF1744',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '600',
                  minHeight: isMobile ? '44px' : 'auto',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                {isLoading ? 'Procesando...' : '❌ Confirmar Rechazo'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}; 